import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { isEmpty } from 'lodash';
import { Modal, Form, notification, Row, Col } from 'antd';

import { queryClient } from '@/Root';

import { FloatingFormItem, InputDecimal } from '@/components/_common';
import {
  DocumentTypeRate,
  DocumentTypeRateFormValues,
} from '@/core/domain/enterprise/DocumentTypeRates/DocumentTypeRates.types';
import { useUpdateDocumentTypeRate } from '@/core/domain/enterprise/DocumentTypeRates/DocumentTypeRates.hooks';

interface Props {
  visible: boolean;
  onCancel: (rate: DocumentTypeRate) => void;
  rate?: DocumentTypeRate;
}
function UpdateRateModal({ visible, onCancel, rate }: Props) {
  const intl = useIntl();
  const [form] = Form.useForm<DocumentTypeRateFormValues>();
  const { documentTypeId } = useRouteMatch<{ documentTypeId: string }>().params;

  const defaultEmptyFieldError = {
    required: true,
    message: <FormattedMessage id="commons.form.errors.requiredField" />,
  };

  useEffect(() => {
    if (visible && !isEmpty(rate)) {
      form.setFieldsValue({
        ...rate,
      });
    }
  }, [visible, rate]);

  const updateMutation = useUpdateDocumentTypeRate(Number(documentTypeId));

  async function handleOk() {
    try {
      const values = await form.validateFields();
      if (rate) {
        const obj = {
          documentTypeId,
          id: rate.id,
          ...values,
        };

        await updateMutation.mutateAsync(obj);
        await queryClient.refetchQueries(['rates', documentTypeId]);

        form.resetFields();

        handleCancel();
      }
    } catch (err) {
      notification.error({
        message: intl.formatMessage({
          id: 'documentTypes.rate.modal.update.error',
        }),
        description: intl.formatMessage({
          id: 'documentTypes.rate.modal.update.errorDescription',
        }),
      });
    }
  }

  function handleCancel() {
    if (rate) {
      onCancel(rate);
    }
  }

  return (
    <Modal
      title={intl.formatMessage({
        id: 'documentTypes.rate.modal.update.title',
      })}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={updateMutation.isLoading}
    >
      <Form form={form}>
        <Row>
          <Col span={24}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'documentTypes.rate.form.percentRate',
              })}
              name="percentRate"
              rules={[defaultEmptyFieldError]}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
        <Form.Item hidden name="numberInstallments" noStyle>
          <InputDecimal />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default UpdateRateModal;
